import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { disableBodyScrolling } from '@/utils/disableBodyScrolling';

@Component
export class ProductFilters extends ThemeComponent {
    @Prop({default: 0}) scrollPosition!: number;

    @Ref() group!: HTMLElement[];
    @Ref() closeFilters!: HTMLButtonElement[];
    @Ref() groupButton!: HTMLButtonElement[];

    init() {

        // Give initial value for scrollPosition
        this.scrollPosition = 0;

        // Show filter options
        this.handleGroupOpen(this.group);

        // Handle keyboard navigation for group buttons
        this.handleGroupKeyboard(this.groupButton);

        // Handle checkbox keyboard events
        this.handleCheckboxKeyboard();

        // Close filter menu
        this.closeFilterMenu(this.closeFilters);
    }

    private handleGroupOpen(groups: HTMLElement[]) {
        for (const group of groups) {
            const button = group.querySelector('.product-filters__group-summary') as HTMLButtonElement;
            if (button) {
                this.$listen(button, 'click', (event) => {
                    event.preventDefault();
                    this.toggleGroup(group, groups);
                });
            }
        }
    }

    private toggleGroup(group: HTMLElement, allGroups: HTMLElement[]) {
        const button = group.querySelector('.product-filters__group-summary') as HTMLButtonElement;
        const isOpen = group.classList.contains('product-filters__group--open');

        // Close all other groups
        for (const sibling of allGroups) {
            if (group !== sibling) {
                sibling.classList.remove('product-filters__group--open');
                const siblingButton = sibling.querySelector('.product-filters__group-summary') as HTMLButtonElement;
                if (siblingButton) {
                    siblingButton.setAttribute('aria-expanded', 'false');
                }
            }
        }

        // Toggle current group
        group.classList.toggle('product-filters__group--open');

        // Update ARIA state
        if (button) {
            button.setAttribute('aria-expanded', (!isOpen).toString());
        }
    }

    private handleGroupKeyboard(buttons: HTMLButtonElement[]) {
        for (const button of buttons) {
            // Handle keyboard events
            this.$listen(button, 'keydown', (event) => {
                const group = button.closest('.product-filters__group') as HTMLElement;
                if (!group) {return;}

                switch (event.key) {
                    case 'Enter':
                    case ' ':
                        event.preventDefault();
                        this.toggleGroup(group, this.group);

                        // Focus first focusable element in the opened group
                        if (group.classList.contains('product-filters__group--open')) {
                            setTimeout(() => {
                                const firstInput = group.querySelector('input[type="checkbox"]') as HTMLElement;
                                if (firstInput) {
                                    firstInput.focus();
                                }
                            }, 50);
                        }
                        break;

                    case 'Escape':
                        event.preventDefault();
                        this.closeAllGroups();
                        button.blur();
                        break;

                    case 'ArrowDown':
                    case 'ArrowRight':
                        event.preventDefault();
                        this.focusNextButton(button, buttons);
                        break;

                    case 'ArrowUp':
                    case 'ArrowLeft':
                        event.preventDefault();
                        this.focusPreviousButton(button, buttons);
                        break;
                }
            });
        }
    }

    private closeAllGroups() {
        for (const group of this.group) {
            group.classList.remove('product-filters__group--open');
            const button = group.querySelector('.product-filters__group-summary') as HTMLButtonElement;
            if (button) {
                button.setAttribute('aria-expanded', 'false');
            }
        }
    }

    private focusNextButton(currentButton: HTMLButtonElement, buttons: HTMLButtonElement[]) {
        const currentIndex = buttons.indexOf(currentButton);
        const nextIndex = (currentIndex + 1) % buttons.length;
        buttons[nextIndex].focus();
    }

    private focusPreviousButton(currentButton: HTMLButtonElement, buttons: HTMLButtonElement[]) {
        const currentIndex = buttons.indexOf(currentButton);
        const previousIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
        buttons[previousIndex].focus();
    }

    private handleCheckboxKeyboard() {
        // Find all checkboxes within filter groups
        const checkboxes = this.querySelectorAll(
            '.product-filters__group-options__list input[type="checkbox"]'
        ) as NodeListOf<HTMLInputElement>;

        for (const checkbox of checkboxes) {
            this.$listen(checkbox, 'keydown', (event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();

                    // Toggle the checkbox (same as clicking)
                    checkbox.checked = !checkbox.checked;

                    // Trigger change event to ensure any existing handlers are called
                    const changeEvent = new Event('change', { bubbles: true });
                    checkbox.dispatchEvent(changeEvent);
                }
            });
        }
    }

    private closeFilterMenu(closeFilters: HTMLButtonElement[]) {
        for (const closeFilter of closeFilters) {
            this.$listen(closeFilter, 'click', () => {

                // Only run if filters are active
                if (!document.body.classList.contains('product-filters-active')) {
                    return;
                }

                // Update value for scrollPosition
                const filterToggle = document.querySelector('.filter-toggle .filter-toggle__button');
                if (filterToggle && filterToggle instanceof HTMLElement) {
                    this.scrollPosition = filterToggle.offsetTop;
                }

                // Enable body scrolling
                this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);

                // Body class
                document.body.classList.remove('product-filters-active');

                // Filters class
                this.classList.remove('product-filters--active');
            });
        }
    }
}
